import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { TelemetryService } from './telemetry.service';

/**
 * MOB-100 Stage 28 — Navigation telemetry tracker.
 *
 * Per the Stage 28 prompt deliverables row:
 *   "Analytics SDK (Mixpanel or PostHog) wired for screen views, key
 *    actions, deep-link opens."
 *
 * Subscribes to `Router.events` and emits `screen.viewed` whenever
 * navigation settles. The reported `screenName` is the STATIC route
 * template (e.g. `/tabs/booths/:id`) not the resolved URL — see
 * `extractRouteTemplate(...)`. That keeps cardinality bounded in PostHog
 * dashboards AND prevents url path segments from becoming a covert PII
 * channel.
 *
 * Bootstrapped from the APP_INITIALIZER chain in `app.config.ts` so the
 * subscription is in place before any route activates.
 */
@Injectable({ providedIn: 'root' })
export class NavigationTrackerService {
  private readonly router = inject(Router);
  private readonly telemetry = inject(TelemetryService);

  private installed = false;

  install(): void {
    if (this.installed) return;
    this.installed = true;
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        const ev = event as NavigationEnd;
        const template = this.extractRouteTemplate(ev.urlAfterRedirects || ev.url);
        this.telemetry.trackScreen(template);
      });
  }

  /**
   * Try to pull the static route template from the activated tree first;
   * fall back to a path-segment normaliser that masks numeric / uuid
   * segments as `:id`. Either way the result is a static string.
   */
  private extractRouteTemplate(resolvedUrl: string): string {
    try {
      const tree = this.router.routerState.root;
      const stack: { segments: string[] }[] = [];
      const walk = (node: typeof tree, acc: string[]): void => {
        const segs = node.snapshot.routeConfig?.path ? [node.snapshot.routeConfig.path] : [];
        const next = [...acc, ...segs];
        stack.push({ segments: next });
        for (const child of node.children) walk(child, next);
      };
      walk(tree, []);
      const deepest = stack[stack.length - 1];
      if (deepest && deepest.segments.length > 0) {
        return '/' + deepest.segments.filter(Boolean).join('/');
      }
    } catch {
      /* fall through to URL normalisation */
    }
    return normaliseUrlToTemplate(resolvedUrl);
  }
}

function normaliseUrlToTemplate(url: string): string {
  let path = url || '/';
  const qIndex = path.indexOf('?');
  if (qIndex >= 0) path = path.slice(0, qIndex);
  return path
    .split('/')
    .map((segment) => {
      if (!segment) return segment;
      if (/^\d+$/.test(segment)) return ':id';
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(segment)) {
        return ':id';
      }
      return segment;
    })
    .join('/');
}
